import {
  Integration,
  IntegrationEnum,
  IntegrationStatus,
} from 'models/InternalIntegrationSetting';
import useEnableIntegrationMutation from 'pages/integrations/hooks/useEnableIntegrationMutation';
import useDisableIntegrationMutation, {
  IntegrationMutationLevel,
} from 'pages/integrations/hooks/useDisableIntegrationMutation';
import { getIntegrationPropertySettingsQueryKey } from 'hooks/useFetchIntegrationPropertySettings';
import { hasExclusiveConnectionRequirement } from 'models/InternalIntegrationSetting.utils';
import { INTEGRATIONS_BLOCKING_INACTIVE_PROPERTY_TOGGLE } from 'pages/integrations/IntegrationsTabs.constants';
import { queryClient } from 'queryClient';
import usePropertySetting from '../usePropertySetting';
import { getPropertiesActivityQueryKey } from '../../../useFetchPropertiesActivity';
import useOAuthPropertyRedirect from './useOAuthPropertyRedirect';

const useToggleProperty = ({
  integration: { enumId, category },
  propertyUid,
  pagePropertyUids,
  isPropertyActive,
}: {
  integration: Integration;
  propertyUid: string;
  pagePropertyUids: string[];
  isPropertyActive: boolean;
}) => {
  const {
    isLoading: isLoadingPropertySetting,
    isIntegrationEnabled,
    isPendingStatus,
    propertySetting,
  } = usePropertySetting({ enumId, propertyUid, pagePropertyUids });

  const { disableIntegration, isLoading: isDisablingIntegration } =
    useDisableIntegrationMutation();
  const { enableIntegration, isLoading: isEnablingIntegration } =
    useEnableIntegrationMutation();
  const { openOauthRedirect, isLoading: isLoadingOauthRedirect } =
    useOAuthPropertyRedirect();

  const { activeAgencyIntegrationSettings, status } = propertySetting ?? {};
  const isNotAvailable = status === IntegrationStatus.NOT_SUPPORTED;

  const queryKey = getIntegrationPropertySettingsQueryKey({
    enumId,
    propertyUids: pagePropertyUids,
  });

  const mutationOptions = {
    onSuccess: () => {
      queryClient.invalidateQueries({
        // Invalidate the toggle all http state after toggling a single property
        queryKey: getPropertiesActivityQueryKey({ enumId }),
      });
    },
  };

  const mutationParams = {
    level: IntegrationMutationLevel.PROPERTY as const,
    enumId,
    propertyUids: [propertyUid],
    appliesToAllProperties: false,
    queryKey,
  };

  const toggleIntegration = () => {
    if (isIntegrationEnabled) {
      disableIntegration(mutationParams, mutationOptions);
      return;
    }

    // Stripe requires a new Stripe connection per property
    if (enumId === IntegrationEnum.STRIPE) {
      openOauthRedirect({ enumId, propertyUid, queryKey });
      return;
    }

    enableIntegration(mutationParams, mutationOptions);
  };

  const isToggleActionBlocked = () => {
    // check if already connected to another payment processor/dynamic pricing tool
    if (hasExclusiveConnectionRequirement(category)) {
      return (
        !!activeAgencyIntegrationSettings?.enumId &&
        activeAgencyIntegrationSettings?.enumId !== enumId
      );
    }

    return false;
  };

  const isInactivePropertyToggleBlocked =
    INTEGRATIONS_BLOCKING_INACTIVE_PROPERTY_TOGGLE.includes(enumId) &&
    !isPropertyActive;

  const isLoading = isLoadingPropertySetting;
  const isUpdating =
    isDisablingIntegration || isEnablingIntegration || isLoadingOauthRedirect;
  const isDisabled =
    isToggleActionBlocked() || isUpdating || isInactivePropertyToggleBlocked;

  return {
    isIntegrationEnabled,
    isPendingStatus,
    toggleIntegration,
    isNotAvailable,
    isDisabled,
    isLoading,
    isUpdating,
  };
};

export default useToggleProperty;
